/**
 * The web app's cookie banner (lib/consent.ts). Three buttons of equal weight (one component, one variant, one size): "Accept all",
 * "Reject non-essential", "Choose". Nothing is ticked before a choice. Shown only when this build sends analytics and no choice or
 * browser signal exists; Your data and privacy reopens it.
 */
// element: cookie-banner (journey: say yes or no to analytics once, then get on with the work)
import { useState } from "react";
import { ANALYTICS_IN_BUILD, readChoice, saveChoice, showBanner } from "../lib/consent.ts";
import { Button } from "./ui.tsx";

export const CONSENT_BUTTONS = ["Accept all", "Reject non-essential", "Choose"] as const;

export function CookieBanner({ force, record, onDone }: { force?: boolean; record?: (analytics: boolean) => Promise<unknown>; onDone?: () => void }) {
  const [open, setOpen] = useState(() => !!force || (ANALYTICS_IN_BUILD && showBanner()));
  const [choosing, setChoosing] = useState(false);
  const [analytics, setAnalytics] = useState(() => readChoice()?.analytics === true);
  if (!open) return null;
  const done = async (via: "accept_all" | "reject" | "choose") => { await saveChoice(via, { analytics }, record); setOpen(false); onDone?.(); };
  return (
    <div className="cookie-banner" role="dialog" aria-labelledby="cookie-banner-h" data-cookie-banner>
      <p id="cookie-banner-h"><strong>Cookies and storage</strong></p>
      <p className="small">The app keeps what it needs to work: your sign-in and your saved work. With your yes, it also counts which screens get used, on our own server only. <a href="/cookies/">What each one is</a>.</p>
      <div className="cookie-banner-row">
        <Button variant="secondary" onClick={() => void done("accept_all")}>{CONSENT_BUTTONS[0]}</Button>
        <Button variant="secondary" onClick={() => void done("reject")}>{CONSENT_BUTTONS[1]}</Button>
        <Button variant="secondary" aria-expanded={choosing} onClick={() => setChoosing(!choosing)}>{CONSENT_BUTTONS[2]}</Button>
      </div>
      {choosing && (
        <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); void done("choose"); }}>
          <p className="small muted">Necessary: sign-in, security, your saved work, this choice. Always on.</p>
          <label className="row"><input type="checkbox" name="analytics" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} /> Analytics: which screens get used (first-party, never sold).</label>
          <div><Button type="submit" variant="secondary">Save my choices</Button></div>
        </form>
      )}
    </div>
  );
}
